import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import EntitiesTable from "../shared/components/EntitiesTable";
import Pagination from "../shared/components/Pagination";
import { getIndustrySpecialistHeaders } from "./industry-specialist-column-definition";
import { useIndustrySpecialists } from "./hooks/useIndustrySpecialists";
import useMutateIndustrySpecialist from "./hooks/useMutateIndustrySpecialist";
import IndustrySpecialistsPageLayout from "./IndustrySpecialistPageLayout";
import useQueryParams from "customHooks/useQueryParams";
import { IndustrySpecialist } from "./models/response/get-industry-specialists-response";
import { QUERY_PARAMS, USADMIN_ENTITIES } from "constants/constants";
import useDebounce from "customHooks/useDebounce";
import BaseError from "../shared/components/BaseError";
import { FilterOption } from "../shared/components/BaseFilters";
import { DEBOUNCE_TIME, ORDER_BY } from "../adminapp.constants";
import { extractFilters } from "../utils/extractFiltersFromSearchParams";
import { INDUSTRY_SPECIALIST_TABLE } from "./industry-specialist.constants";
import DeletionModal from "../shared/components/DeletionModal";

const IndustrySpecialists = () => {
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const initialPage = parseInt(
    searchParams.get(QUERY_PARAMS.PAGE) || QUERY_PARAMS.INITIAL_PAGE,
    10
  );
  const initialSortBy =
    searchParams.get(QUERY_PARAMS.SORT_BY) || QUERY_PARAMS.BY_ID;
  const initialOrder =
    (searchParams.get(QUERY_PARAMS.ORDER_BY) as ORDER_BY) || ORDER_BY.DESC;
  const initialFilters = useMemo(
    () => extractFilters(searchParams),
    [searchParams]
  );
  const initialSearchQuery = searchParams.get(QUERY_PARAMS.SEARCH_QUERY) || "";

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [orderBy, setOrderBy] = useState<ORDER_BY>(initialOrder);
  const [filters, setFilters] =
    useState<Record<string, string>>(initialFilters);

  const [searchInput, setSearchInput] = useState<string>(initialSearchQuery);
  const [searchQuery, setSearchQuery] = useState<string>(initialSearchQuery);
  const debouncedSearchInput = useDebounce(searchInput, DEBOUNCE_TIME);

  useEffect(() => {
    setSearchQuery(debouncedSearchInput);
    setCurrentPage(1);
  }, [debouncedSearchInput]);

  const handleFilterChange = (
    mainFilter: FilterOption | null,
    subcategory: FilterOption | null
  ) => {
    if (mainFilter && subcategory) {
      setFilters((prev) => ({
        ...prev,
        [mainFilter.value]: subcategory.value,
      }));
      setCurrentPage(1);
    }
  };

  const handleRemoveFilter = (filterKey: string) => {
    setFilters((prev) => {
      const { [filterKey]: removed, ...newFilters } = prev;
      return newFilters;
    });

    setCurrentPage(1);
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [entityName, setEntityName] = useState("");
  const headers = getIndustrySpecialistHeaders(intl);

  const queryParams = useQueryParams({
    currentPage,
    sortBy,
    orderBy,
    filters,
    searchQuery,
  });

  useEffect(() => {
    if (queryParams !== location.search.substring(1)) {
      history.replace({ search: "?" + queryParams });
    }
  }, [queryParams, history, location.search]);

  const { data, error, isLoading, isError, refetch } = useIndustrySpecialists({
    currentPage,
    sortBy,
    orderBy,
    filters,
    searchQuery,
  });

  const totalPages = data?.meta?.totalPages || 1;

  const {
    deleteIndustrySpecialist,
    deleteIndustrySpecialistIsError,
    deleteIndustrySpecialistLoading,
    deleteIndustrySpecialistReset,
    deleteIndustrySpecialistSuccess,
  } = useMutateIndustrySpecialist({
    id: selectedEntityId as string,
  });

  const handleDelete = (entityId: string) => {
    const industrySpecialist = data?.data.find(
      (item) => item.id.toString() === entityId
    );
    const industrySpecialistName = industrySpecialist?.name || "";
    setSelectedEntityId(entityId);
    setShowDeleteModal(true);
    setEntityName(industrySpecialistName);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
  };

  const confirmDelete = () => {
    if (selectedEntityId) {
      deleteIndustrySpecialist({ industrySpecialistId: selectedEntityId });
    }
    closeModal();
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setOrderBy(orderBy === ORDER_BY.ASC ? ORDER_BY.DESC : ORDER_BY.ASC);
    } else {
      setSortBy(field);
      setOrderBy(ORDER_BY.DESC);
    }
  };

  if (isError) {
    return <BaseError errorMessage={error} onRetry={refetch} />;
  }

  return (
    <IndustrySpecialistsPageLayout
      searchInput={searchInput}
      onSearchInputChange={setSearchInput}
      onFilterChange={handleFilterChange}
      appliedFilters={filters}
      onRemoveFilter={handleRemoveFilter}
    >
      <DeletionModal
        isOpen={
          showDeleteModal ||
          deleteIndustrySpecialistIsError ||
          deleteIndustrySpecialistSuccess
        }
        isError={deleteIndustrySpecialistIsError}
        isSuccess={deleteIndustrySpecialistSuccess}
        loading={deleteIndustrySpecialistLoading}
        deletedEntityName={entityName}
        onClose={() => {
          setShowDeleteModal(false);
          deleteIndustrySpecialistReset();
        }}
        onConfirm={confirmDelete}
      />
      <EntitiesTable<IndustrySpecialist>
        headers={headers}
        data={data?.data || []}
        entityType={USADMIN_ENTITIES.INDUSTRY_SPECIALISTS}
        onDelete={handleDelete}
        sortBy={sortBy}
        order={orderBy}
        onSort={handleSort}
        loading={isLoading}
        loaderDimensions={{
          mobile: {
            rows: INDUSTRY_SPECIALIST_TABLE.MOBILE_ROWS,
            columns: INDUSTRY_SPECIALIST_TABLE.MOBILE_COLUMNS,
          },
          desktop: {
            rows: INDUSTRY_SPECIALIST_TABLE.DESKTOP_ROWS,
            columns: INDUSTRY_SPECIALIST_TABLE.DESKTOP_COLUMNS,
          },
        }}
      />
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          pageCount={totalPages}
          onPageChange={(page: number) => setCurrentPage(page)}
        />
      )}
    </IndustrySpecialistsPageLayout>
  );
};

export default IndustrySpecialists;
